/**
 * 订单通用业务逻辑 Hook（对应原 common-fun-hook.ts）
 * 封装订单操作：出发、到达、打卡、开始服务、完成等
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "antd-mobile";
import {
  arriveOrder,
  departOrder,
  departVerify,
  finishVerify,
  punchCardVerify,
  startService,
} from "@/api/staff";
import { toastSuccess, toastWarning } from "@/utils/toast";
import { requestLocationOnUserAction } from "@/utils/location";

export function useOrderCommonFun(orderId: number, extra?: { latitude?: string; longitude?: string }) {
  const navigate = useNavigate();

  const orderDetail = () => navigate(`/workspace/order/detail?id=${orderId}`);

  // 出发
  const handleDepart = async (cb?: () => void) => {
    Dialog.show({
      content: "是否准备出发？",
      closeOnAction: true,
      actions: [
        [
          {
            key: "cancel",
            text: "取消",
            style: { color: "#FF6B35", fontWeight: 500 },
            onClick: () => {},
          },
          {
            key: "confirm",
            text: "确认",
            bold: true,
            style: { color: "#FF6B35", fontWeight: 600 },
            onClick: async () => {
              await departVerify(orderId);
              await departOrder(orderId);
              toastSuccess("已出发！");
              setTimeout(() => cb?.(), 800);
            },
          },
        ],
      ],
    });
  };

  // 报备
  const handleReport = () => navigate(`/workspace/order/report?id=${orderId}`);

  // 到达
  const handleStaffArrive = async (cb?: () => void) => {
    Dialog.show({
      content: "是否已到达？",
      closeOnAction: true,
      actions: [
        [
          {
            key: "cancel",
            text: "取消",
            style: { color: "#FF6B35", fontWeight: 500 },
            onClick: () => {},
          },
          {
            key: "confirm",
            text: "确认",
            bold: true,
            style: { color: "#FF6B35", fontWeight: 600 },
            onClick: async () => {
              await arriveOrder(orderId);
              cb?.();
            },
          },
        ],
      ],
    });
  };

  // 打卡定位检测（H5：浏览器定位）
  const handleStaffLocationVerify = async (cb?: () => void) => {
    try {
      toastWarning("正在定位...");
      // 统一走定位服务（内部会按环境选择微信 JS-SDK / 浏览器定位，并用高德 WebService 逆地理补地址）
      const locRes = await requestLocationOnUserAction();
      if (!locRes.success || !locRes.location) {
        toastWarning(locRes.error || "定位失败，请检查定位权限或网络");
        return;
      }

      const lat1 = Number(locRes.location.latitude);
      const lon1 = Number(locRes.location.longitude);
      const lat2 = Number(extra?.latitude);
      const lon2 = Number(extra?.longitude);
      if (!Number.isFinite(lat2) || !Number.isFinite(lon2)) {
        // 无目标点坐标时，不拦截流程（避免阻塞）
        cb?.();
        return;
      }

      await punchCardVerify({ lat1, lon1, lat2, lon2 });
      cb?.();
    } catch {
      toastWarning("定位失败，请检查定位权限或网络");
    }
  };

  // 开始服务
  const handleStaffStart = async (cb?: () => void) => {
    Dialog.show({
      content: "是否开始服务？",
      closeOnAction: true,
      actions: [
        [
          {
            key: "cancel",
            text: "取消",
            style: { color: "#FF6B35", fontWeight: 500 },
            onClick: () => {},
          },
          {
            key: "confirm",
            text: "确认",
            bold: true,
            style: { color: "#FF6B35", fontWeight: 600 },
            onClick: async () => {
              await startService(orderId);
              cb?.();
            },
          },
        ],
      ],
    });
  };

  // 完成服务验证
  const handleStaffFinish = async (cb?: () => void) => {
    const res = await finishVerify(orderId);
    if (!res?.is_finish) return;
    Dialog.show({
      content: res.msg || "确认完成服务？",
      closeOnAction: true,
      actions: [
        [
          {
            key: "cancel",
            text: "取消",
            style: { color: "#FF6B35", fontWeight: 500 },
            onClick: () => {},
          },
          {
            key: "confirm",
            text: "确认",
            bold: true,
            style: { color: "#FF6B35", fontWeight: 600 },
            onClick: async () => {
              await handleStaffLocationVerify(cb);
            },
          },
        ],
      ],
    });
  };

  return {
    orderDetail,
    handleReport,
    handleDepart,
    handleStaffArrive,
    handleStaffStart,
    handleStaffFinish,
    handleStaffLocationVerify,
  };
}
